import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import Seo, { faqJsonLd } from "@/components/Seo";

export const FAQ_ITEMS = [
  {
    category: "Getting Started",
    q: "What is ADN Local FanFest?",
    a: "ADN Local FanFest is a free citywide watch-party sweepstakes running in Jacksonville, Florida from July 6 to July 19, 2026. Fans register for free, check in at local Watch Party Partner businesses, and enter to win daily prizes plus a Grand Prize — 6 days / 5 nights in Cancun for two with airfare and $500 cash — drawn live after the World Cup Final.",
  },
  {
    category: "Getting Started",
    q: "How do I enter?",
    a: "Go to the Register page, fill out a 60-second form with your name, email, phone, ZIP code, and date of birth, then accept the Official Rules and Privacy Policy. That's it — you're entered.",
  },
  {
    category: "Getting Started",
    q: "How much does it cost?",
    a: "Nothing. Entry is completely free. No purchase is necessary and a purchase will not increase your chances of winning.",
  },
  {
    category: "Getting Started",
    q: "Who is eligible?",
    a: "Anyone 18 years of age or older at the time of entry can register and enter to win. However, prizes involving travel, hotel stays, or alcohol require the winner to be at least 21 years old at time of claim (industry-standard hotel and travel policy). Entrants aged 18–20 can still win non-travel prizes such as gift cards and restaurant certificates. One entry per person / household. Void where prohibited or restricted by law, including Puerto Rico, Guam, the U.S. Virgin Islands, American Samoa, and the Northern Mariana Islands.",
  },

  {
    category: "Prizes",
    q: "What is the Grand Prize?",
    a: "6 days / 5 nights in Cancun, Mexico for two — includes roundtrip airfare for two (approximately $800 per person / $1,600 total) plus a $500 USD cash award for the winner. The winner is responsible ONLY for nightly taxes and resort fees; all other trip costs are covered. Approximate retail value: $2,999. Drawn live after the World Cup Final on July 19, 2026. Winner must be 21 years of age or older at time of claim. If a selected winner does not meet the age requirement or cannot travel, they will receive a cash award of $2,999 in lieu of the trip (Cash Alternative Clause).",
  },
  {
    category: "Prizes",
    q: "What daily prizes are available?",
    a: "13 days of drawings from July 6 through July 18, 2026 including $100 restaurant certificates, Jacksonville hotel weekend stays, travel and experience certificates, and $50 sponsor gift cards.",
  },
  {
    category: "Prizes",
    q: "Do I need to be present to win daily prizes?",
    a: "No. Daily prize winners are notified by email and/or SMS and have until the listed claim deadline to respond. You do NOT need to be at a Watch Party Partner to win a daily prize.",
  },
  {
    category: "Prizes",
    q: "What if a winner doesn't claim their prize?",
    a: "If a selected winner does not respond by the claim deadline or does not meet all eligibility requirements, we may select an alternate winner from the same eligible pool.",
  },
  {
    category: "Prizes",
    q: "Am I responsible for taxes on the prize?",
    a: "Yes. Winners are responsible for all applicable federal, state, and local taxes. Prize values may be reportable to the IRS.",
  },

  {
    category: "Check-Ins",
    q: "How does check-in work?",
    a: "At any Official Watch Party Partner, open the ADN app on your phone, tap Check In, and scan the ADN QR code posted at the location. GPS confirms you're on-site and your digital Fan Passport stamp is added instantly.",
  },
  {
    category: "Check-Ins",
    q: "What is the Fan Passport?",
    a: "The Fan Passport is your digital collection of stamps — one per unique Watch Party Partner you visit. Collect more stamps to unlock category badges, offers, and prize eligibility.",
  },
  {
    category: "Check-Ins",
    q: "How do I qualify for the Grand Prize drawing?",
    a: "You must be registered AND check in via the ADN app at any Official Watch Party Partner between 12:00 PM and 2:45 PM EDT on Sunday, July 19, 2026. The World Cup Final kicks off at 3:00 PM EDT and the Grand Prize is drawn live after the match.",
  },
  {
    category: "Check-Ins",
    q: "Where do I find Watch Party Partners?",
    a: "Visit the Directory to browse all participating businesses, filter by category (sports bars, breweries, restaurants, hotels, food trucks, and more), or view them on the interactive Jacksonville map.",
  },

  {
    category: "Businesses",
    q: "How can my business become a sponsor?",
    a: "Visit the Sponsors page. Choose from four one-time packages — Bronze $250, Silver $1,000, Gold $2,500, or Title $5,000 — pay securely via Stripe, and your tier activates immediately.",
  },
  {
    category: "Businesses",
    q: "What do sponsors receive?",
    a: "A featured directory listing, a printable QR check-in code, real-time analytics on scans and coupon redemptions, marketing-consent leads, and inclusion in citywide FanFest promotion.",
  },

  {
    category: "Privacy & Legal",
    q: "Do you sell my information?",
    a: "No. ADN Local does not sell or rent consumer information. See our Privacy Policy for full details.",
  },
  {
    category: "Privacy & Legal",
    q: "Do I have to opt in to marketing to enter?",
    a: "No. Marketing consent is optional and completely separate from your sweepstakes entry. You can enter and win without opting in to any marketing messages.",
  },
  {
    category: "Privacy & Legal",
    q: "Is ADN Local affiliated with FIFA or the World Cup?",
    a: "No. ADN Local is not sponsored by or affiliated with FIFA, the World Cup, any soccer federation, airline, hotel, or travel provider unless expressly stated. FIFA and World Cup marks belong to their respective owners.",
  },
];

const CATEGORIES = ["All", "Getting Started", "Prizes", "Check-Ins", "Businesses", "Privacy & Legal"];

export default function FAQ() {
  const [cat, setCat] = useState("All");
  const [openIdx, setOpenIdx] = useState(0);
  const items = cat === "All" ? FAQ_ITEMS : FAQ_ITEMS.filter(i => i.category === cat);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <Seo
        title="FAQ — Frequently Asked Questions"
        description="Answers to common questions about ADN Local FanFest: how to enter, prize details, check-in eligibility, sponsor packages, taxes, and privacy. Free to enter, no purchase necessary, must be 18+."
        jsonLd={faqJsonLd(FAQ_ITEMS)}
        keywords="ADN Local FAQ, FanFest questions, how to enter sweepstakes, Cancun prize rules, Jacksonville watch party"
      />
      <div className="text-xs tracking-[0.3em] uppercase text-primary">Help Center</div>
      <h1 className="font-bebas text-5xl sm:text-6xl mt-2">FREQUENTLY ASKED QUESTIONS</h1>
      <p className="text-white/70 mt-3 max-w-2xl">
        Everything you need to know about entering, checking in, prize eligibility, and how businesses get involved.
        Can't find your answer? Email <a className="text-primary underline" href="mailto:support@myadnlocal.com">support@myadnlocal.com</a>.
      </p>

      <nav className="mt-8 flex flex-wrap gap-2" aria-label="FAQ categories">
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => { setCat(c); setOpenIdx(-1); }}
            data-testid={`faq-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}
            className={`pill-btn ${cat === c ? "active" : ""}`}>{c}</button>
        ))}
      </nav>

      <div className="mt-6 space-y-3" itemScope itemType="https://schema.org/FAQPage">
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <article key={`${cat}-${i}`}
              itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
              className="adn-card">
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                aria-expanded={open}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-start justify-between gap-4 p-5 text-left">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-primary mb-1">{item.category}</div>
                  <h2 itemProp="name" className="font-bebas text-xl sm:text-2xl">{item.q}</h2>
                </div>
                <ChevronDown size={20}
                  className={`shrink-0 mt-1 transition-transform ${open ? "rotate-180 text-primary" : "text-white/60"}`}/>
              </button>
              <div
                itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
                className={`px-5 pb-5 -mt-2 ${open ? "" : "hidden"}`}
              >
                <p itemProp="text" className="text-white/80 leading-relaxed text-sm sm:text-base">{item.a}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-14 adn-card p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-1">
          <h3 className="font-bebas text-3xl">READY TO ENTER?</h3>
          <p className="text-white/70 mt-1">Free entry. 60 seconds. Must be 18+.</p>
        </div>
        <Link to="/register" data-testid="faq-register-cta"
          className="inline-flex items-center gap-2 px-6 py-4 bg-primary text-black font-bebas text-xl rounded-sm hover:brightness-110">
          ENTER FREE <ArrowRight size={16}/>
        </Link>
      </div>
    </div>
  );
}
